import { ChevronUp, ChevronDown, ChevronsUpDown, Inbox } from "lucide-react";
import { cn } from "@/lib/utils";
import { EmissionUnitText } from "./EmissionUnitText";
import { EmptyState } from "./EmptyState";
import Button from "@mui/material/Button";

export function DataTableError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => unknown;
}) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-destructive/30 bg-card p-6 text-sm"
    >
      <p>{message}</p>
      <Button
        onClick={() => void onRetry()}
        size="small"
        variant="outlined"
        sx={{ mt: 2 }}
      >
        Retry
      </Button>
    </div>
  );
}

interface DataTableProps {
  children?: React.ReactNode;
  isLoading?: boolean;
  loadingRows?: number;
  total?: number;
  itemName?: string;
}

export function DataTable({
  children,
  isLoading = false,
  loadingRows = 5,
  total,
  itemName = "records",
}: DataTableProps) {
  if (isLoading) {
    return (
      <div className="rounded-xl border border-border/50 shadow-card bg-card overflow-hidden">
        <div className="px-4 py-3 bg-gradient-to-b from-muted/60 to-muted/30 border-b border-border/50">
          <div className="h-3 w-24 rounded bg-muted-foreground/10 animate-pulse" />
        </div>
        <div className="divide-y divide-border/20">
          {Array.from({ length: loadingRows }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-4 py-3.5">
              <div
                className="h-4 w-4 rounded bg-muted-foreground/8 animate-pulse"
                style={{ animationDelay: `${i * 0.05}s` }}
              />
              <div
                className="h-4 rounded bg-muted-foreground/8 animate-pulse flex-[2]"
                style={{ animationDelay: `${i * 0.05 + 0.02}s` }}
              />
              <div
                className="h-4 rounded bg-muted-foreground/8 animate-pulse flex-[1.5]"
                style={{ animationDelay: `${i * 0.05 + 0.04}s` }}
              />
              <div
                className="h-4 rounded bg-muted-foreground/8 animate-pulse flex-1"
                style={{ animationDelay: `${i * 0.05 + 0.06}s` }}
              />
              <div
                className="h-4 rounded bg-muted-foreground/8 animate-pulse flex-1"
                style={{ animationDelay: `${i * 0.05 + 0.08}s` }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="overflow-auto"
      data-ai-table-label={itemName}
      data-ai-context-summary={
        total === undefined
          ? undefined
          : `${total} total ${itemName}. Only rows rendered on this page are included in page context.`
      }
    >
      {children}
    </div>
  );
}

interface SortHeaderProps {
  field: string;
  activeField?: string;
  sortOrder?: string;
  align?: "left" | "right";
  onSort: (field: string) => void;
  children: React.ReactNode;
  className?: string;
}

export function SortHeader({
  field,
  activeField,
  sortOrder,
  align = "left",
  onSort,
  children,
  className,
}: SortHeaderProps) {
  const isActive = activeField === field;
  const isAsc = sortOrder === "asc" || sortOrder === "ASC";

  return (
    <th
      aria-sort={isActive ? (isAsc ? "ascending" : "descending") : undefined}
      className={cn(
        "select-none",
        isActive && "text-primary",
        align === "right" && "text-right",
        className,
      )}
    >
      <button
        type="button"
        className={cn("table-sort-trigger", align === "right" && "justify-end")}
        onClick={() => onSort(field)}
      >
        <span className="font-semibold transition-colors">
          {typeof children === "string" ? (
            <EmissionUnitText text={children} />
          ) : (
            children
          )}
        </span>
        {isActive ? (
          <span
            aria-hidden="true"
            className={cn(
              "inline-flex items-center justify-center",
              "h-5 min-w-5 px-1 rounded-md",
              "bg-primary/20 text-primary",
              "transition-[color,background-color,border-color,box-shadow,opacity,transform,translate,scale,rotate] duration-200",
            )}
          >
            {isAsc ? (
              <ChevronUp className="h-3.5 w-3.5" strokeWidth={3} />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" strokeWidth={3} />
            )}
          </span>
        ) : (
          <ChevronsUpDown
            aria-hidden="true"
            className="table-sort-hint h-3.5 w-3.5 shrink-0"
          />
        )}
      </button>
    </th>
  );
}

interface DataTableEmptyProps {
  message: string;
  description?: string;
  action?: React.ReactNode;
}

export function DataTableEmpty({
  message,
  description,
  action,
}: DataTableEmptyProps) {
  return (
    <EmptyState
      icon={Inbox}
      title={message}
      description={description}
      action={action}
    />
  );
}
